import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Tooltip } from '@mui/material';
import ProductGridComponent from './ProductGridComponent';
import ProductListComponent from './ProductListComponent';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { Check } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { EXPORT_SIMULATION_MUTATION } from '../../../../document/edit/documents/mutations';
import { useGlobalSubscription } from '../../../../../providers/GlobalCacheManager';
import { EXPORT_SIMULATION_EXECUTED } from '../../../../document/edit/documents/subscriptions';
import { buildExportDownloadUrl } from '../../../../document/edit/documents/actionCreators';
import { useServerErrorHandling } from '../../../../../utils/hooks/useServerErrorHandling';
import ThinDivider from 'components/common/ThinDivider';
import { ProductServiceMonthyValueWrapper } from './ProductSelectionStyles';
import { useToast } from 'utils/hooks';
import { getInt } from "utils/functions";

const ProductComponent = props => {
  const { productsViewMode, areGraceRatesVisible, product, ...rest } = props;
 const { t } = useTranslation();
  const addToast = useToast();
  
  const [gridMode, setGridMode] = useState();
  useEffect(() => {
    setGridMode(productsViewMode === 0);
  }, [productsViewMode]);

  const [graceList, setGraceList] = useState([]);
  
  const hasProduct = useMemo(() => getInt(product?.documentId) !== null && getInt(product?.siteId) !== null && getInt(product?.simulationDocumentId) !== null, [product?.documentId, product?.siteId, product?.simulationDocumentId])
 
  useEffect(() => {
    if (areGraceRatesVisible && product?.graceRates) {
      let tmpGraceList = [];
      if (product?.graceRates?.['fullGracePeriod'] !== 0)
        tmpGraceList.push({
          name: `${t('OfferEdit.FinancialProducts.FullGracePeriod')}`,
          value: `${product.graceRates['fullGracePeriod']}`
        });
      if (product?.graceRates?.['capitalGrace'] !== 0) {
        let tmpName = `${t('OfferEdit.FinancialProducts.CapitalGrace')}`;
        if (tmpGraceList.length > 0) tmpName = `${t('OfferEdit.FinancialProducts.Capital')}`;
        tmpGraceList.push({ name: tmpName, value: `${product.graceRates['capitalGrace']}` });
      }
      if (product?.graceRates?.['interestGrace'] !== 0) {
        let tmpName = `${t('OfferEdit.FinancialProducts.InterestGrace')}`;
        if (tmpGraceList.length > 0) tmpName = `${t('OfferEdit.FinancialProducts.Interest')}`;
        tmpGraceList.push({ name: tmpName, value: `${product.graceRates['interestGrace']}` });
      }
      if (tmpGraceList.length > 0) setGraceList(tmpGraceList);
    }
  }, [areGraceRatesVisible, product?.graceRates, t]);

  /* EXPORT SIMULATION */
  const [exportingSimulation, setExportingSimulation] = useState(false);
  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setExportingSimulation(false);
    }
  });

  const [exportSimulationCall] = useMutation(EXPORT_SIMULATION_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setExportingSimulation(false);
    },
    onCompleted: data => {
      const { correlationId } = data.exportSimulation;
      storeKey({ correlationId });
      setExportingSimulation(true);
    }
  });

  const onExportSimulation = () => {
    async function onExportSimulationAsync() {
      exportSimulationCall({
        variables: {
          documentId: product.documentId,
          siteId: product.siteId,
          simulationId: product.simulationDocumentId
        }
      });
    }
    onExportSimulationAsync();
  };

  useGlobalSubscription(EXPORT_SIMULATION_EXECUTED, {
    skip: !hasProduct,
    variables: {
      documentId: product.documentId,
      siteId: product.siteId,
      simulationId: product.simulationDocumentId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setExportingSimulation(false);
      const url = buildExportDownloadUrl(data.exportSimulationExecuted.filePath, data.exportSimulationExecuted.fileName)();
      window.location = url;
    }
  });
  /* END EXPORT SIMULATION */

  /* ProductIncludedServices */
  const ProductIncludedServices = ({ services }) => {
    return (
      <React.Fragment>
        {services.map((service, index) => {
          return (
            <>
              <Grid container key={index} alignItems='center'>
                <Grid item xs={9}>
                  <span>{service.categoryName}</span>
                </Grid>
                <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'end' }}>
                  {service.errorMessage ? (
                    <Tooltip title={service.errorMessage}>
                      <span>
                        <PriorityHighIcon fontSize='small' color='error' />
                      </span>
                    </Tooltip>
                  ) : (
                    <ProductServiceMonthyValueWrapper>
                      {service.monthlyValue > 0 ? (
                        <>
                          <Check color='success' />
                          {gridMode && t('OfferEdit.FinancialProducts.Included')}
                        </>
                      ) : (
                        <>
                          <CloseIcon color='error' />
                          {gridMode && t('OfferEdit.FinancialProducts.NotIncluded')}
                        </>
                      )}
                    </ProductServiceMonthyValueWrapper>
                  )}
                </Grid>
              </Grid>
              {index !== services.length - 1 && <ThinDivider light />}
            </>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <>
      {gridMode ? (
        <ProductGridComponent
          ProductIncludedServices={ProductIncludedServices}
          areGraceRatesVisible={areGraceRatesVisible}
          product={product}
          graceList={graceList}
          exportSimulation={onExportSimulation}
          exportingSimulation={exportingSimulation}
          {...rest}
        />
      ) : (
        <ProductListComponent
          ProductIncludedServices={ProductIncludedServices}
          areGraceRatesVisible={areGraceRatesVisible}
          product={product}
          graceList={graceList}
          exportSimulation={onExportSimulation}
          exportingSimulation={exportingSimulation}
          {...rest}
        />
      )}
    </>
  );
};

ProductComponent.propTypes = {
  productsViewMode: PropTypes.number,
  areGraceRatesVisible: PropTypes.bool.isRequired,
  product: PropTypes.object.isRequired,
  services: PropTypes.array
};

export default ProductComponent;
